import { useRef, useState } from "react";
import { CircleAlert, CircleCheck, Paperclip, Send } from "lucide-react";
import {
  applicationEndpoint,
  applicationFiles,
  applicationFileTypes,
  applicationMaxMb,
  careersEmail,
  careersMailto,
} from "../../data/careers";
import { cx } from "../../lib/cx";

const fieldClass =
  "w-full rounded-brand border border-line-strong bg-white px-4 py-3 text-[16px] text-ink transition-colors placeholder:text-subtle focus:border-blue focus:outline-none focus-visible:outline-none focus:ring-2 focus:ring-blue/30 aria-[invalid=true]:border-red-600";

const megabytes = (bytes) => bytes / (1024 * 1024);
const extension = (name = "") => name.toLowerCase().match(/\.[a-z0-9]+$/)?.[0];

/** Problems with the form, keyed by field name, in display order. */
function validate(data) {
  const errors = {};
  if (!data.get("name")?.trim()) errors.name = "Please enter your name.";
  const email = data.get("email")?.trim();
  if (!email) errors.email = "Please enter your email address.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
    errors.email = "That email address doesn't look right.";
  for (const { name, label, required } of applicationFiles) {
    const file = data.get(name);
    const present = file && file.size > 0;
    if (!present) {
      if (required) errors[name] = `Please attach your ${label.toLowerCase()}.`;
      continue;
    }
    if (!applicationFileTypes.includes(extension(file.name)))
      errors[name] = "Please attach a PDF or Word document.";
    else if (megabytes(file.size) > applicationMaxMb)
      errors[name] = `Files can be up to ${applicationMaxMb} MB.`;
  }
  return errors;
}

function Field({ id, label, required, error, hint, children }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block font-bold text-ink">
        {label}
        {required ? (
          <span aria-hidden="true" className="text-blue-deep">
            {" "}
            *
          </span>
        ) : (
          <span className="font-normal text-muted"> (optional)</span>
        )}
      </label>
      {children}
      {hint && !error && (
        <p id={`${id}-hint`} className="mt-1.5 text-sm text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={`${id}-error`}
          className="mt-1.5 flex items-center gap-1.5 text-sm font-bold text-red-700"
        >
          <CircleAlert size={15} aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The job application, like the old site's: name, email, a message, a
 * resume and a cover letter. It posts to the careers endpoint (PIT), which
 * emails it to HR and adds the applicant to the posting's hiring role; until
 * one is configured, or if sending fails, applicants are told to email their
 * resume instead.
 */
export default function ApplicationForm({ job }) {
  const position = job.title;
  const form = useRef(null);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const emailFallback = careersMailto(`Application: ${position}`);

  async function submit(event) {
    event.preventDefault();
    const data = new FormData(form.current);
    const found = validate(data);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      form.current.elements[first]?.focus();
      return;
    }
    if (!applicationEndpoint) {
      setStatus("unavailable");
      return;
    }
    setStatus("sending");
    // Browsers send an empty file for an optional file input left blank;
    // leave it out rather than send a 0-byte "cover letter".
    for (const { name } of applicationFiles)
      if (!data.get(name)?.size) data.delete(name);
    // Which posting this is for. PIT files the applicant under the hiring
    // role keyed by `position_key` (the posting's address), creating the role
    // from these details the first time someone applies.
    data.set("position", position);
    data.set("position_key", job.path);
    for (const [field, value] of [
      ["position_type", job.type],
      ["position_location", job.location],
      ["position_summary", job.summary],
    ])
      if (value) data.set(field, value);
    data.set("page_url", window.location.href);
    try {
      const response = await fetch(applicationEndpoint, {
        method: "POST",
        body: data,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      form.current.reset();
      setStatus("sent");
    } catch {
      setStatus("failed");
    }
  }

  if (status === "sent")
    return (
      <div
        role="status"
        className="rounded-brand border border-line bg-white p-8 text-center shadow-soft"
      >
        <CircleCheck
          size={40}
          aria-hidden="true"
          className="mx-auto text-blue-deep"
        />
        <h3 className="font-display mt-3 text-[28px] text-ink uppercase">
          Application received
        </h3>
        <p className="mx-auto mt-2 max-w-[460px] text-muted">
          Thanks for applying for {position}. We read every application and will
          be in touch by email.
        </p>
      </div>
    );

  const describedBy = (name, hint) =>
    errors[name] ? `${name}-error` : hint ? `${name}-hint` : undefined;

  return (
    <form
      ref={form}
      onSubmit={submit}
      noValidate
      encType="multipart/form-data"
      className="relative rounded-brand border border-line bg-white p-6 shadow-soft sm:p-8"
    >
      <p className="mb-6 text-sm text-muted">
        Fields marked <span className="text-blue-deep">*</span> are required.
      </p>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field id="name" label="Name" required error={errors.name}>
          <input
            id="name"
            name="name"
            autoComplete="name"
            required
            aria-invalid={Boolean(errors.name)}
            aria-describedby={describedBy("name")}
            className={fieldClass}
          />
        </Field>
        <Field id="email" label="Email" required error={errors.email}>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            aria-invalid={Boolean(errors.email)}
            aria-describedby={describedBy("email")}
            className={fieldClass}
          />
        </Field>
      </div>
      <div className="mt-5">
        <Field id="message" label="Message" error={errors.message}>
          <textarea
            id="message"
            name="message"
            rows={5}
            placeholder="Anything you'd like us to know — about you, your work, or why this role."
            className={cx(fieldClass, "resize-y")}
          />
        </Field>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
        {applicationFiles.map(({ name, label, required }) => (
          <Field
            key={name}
            id={name}
            label={label}
            required={required}
            error={errors[name]}
            hint={`PDF or Word, up to ${applicationMaxMb} MB.`}
          >
            <div className="relative">
              <Paperclip
                size={17}
                aria-hidden="true"
                className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-muted"
              />
              <input
                id={name}
                name={name}
                type="file"
                accept={applicationFileTypes.join(",")}
                required={required}
                aria-invalid={Boolean(errors[name])}
                aria-describedby={describedBy(name, true)}
                className={cx(
                  fieldClass,
                  "pl-11 text-[15px] file:mr-3 file:rounded-md file:border-0 file:bg-surface file:px-3 file:py-1.5 file:font-bold file:text-ink",
                )}
              />
            </div>
          </Field>
        ))}
      </div>
      {/* Spam trap: hidden from people, filled in by bots. */}
      <div aria-hidden="true" className="absolute -left-[9999px]">
        <label>
          Leave this empty
          <input name="website" tabIndex={-1} autoComplete="off" />
        </label>
      </div>

      {(status === "failed" || status === "unavailable") && (
        <div
          role="alert"
          className="mt-6 flex gap-3 rounded-brand border border-yellow bg-yellow-soft p-4 text-[15px] text-ink"
        >
          <CircleAlert
            size={20}
            aria-hidden="true"
            className="mt-0.5 flex-none text-yellow-ink"
          />
          <p>
            {status === "failed"
              ? "Sorry — your application didn't go through."
              : "Online applications are being set up."}{" "}
            Please email your resume and cover letter to{" "}
            <a
              href={emailFallback}
              className="font-bold text-blue-deep underline decoration-yellow decoration-2 underline-offset-4"
            >
              {careersEmail}
            </a>{" "}
            with the subject “Application: {position}”.
          </p>
        </div>
      )}

      <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
        <button
          type="submit"
          disabled={status === "sending"}
          className="inline-flex items-center justify-center gap-2 rounded-brand bg-yellow px-[26px] py-[13px] font-bold text-slate transition duration-150 hover:-translate-y-px hover:shadow-glow disabled:translate-y-0 disabled:opacity-60 disabled:shadow-none"
        >
          <Send size={17} aria-hidden="true" />
          {status === "sending" ? "Sending…" : "Submit application"}
        </button>
        <p className="text-sm text-muted">
          Questions first?{" "}
          <a
            href={careersMailto(`Question about the ${position} role`)}
            className="font-bold text-blue-deep hover:underline"
          >
            Email us
          </a>
          .
        </p>
      </div>
    </form>
  );
}
